const API_BASE = '/api/v1/public/projects';

export interface ContactInfo {
  contactRequested: boolean;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export interface NamingContext {
  streetLabel:   string | null;
  streetName:    string | null;
  fromStreet:    string | null;
  toStreet:      string | null;
  confidence:    string;
  source:        string;
  warnings:      string[];
}

export interface ContributionPayload {
  scale: 'city' | 'sector' | 'place';
  category: string;
  comment?: string;
  geometry?: Record<string, unknown>;
  canonicalEdgeId?: string;
  h3CellId?: string;
  sessionId?: string;
  source?: 'public_site' | 'workshop';
  contactInfo?: ContactInfo;
  accessibleMode?: boolean;
  locationDescription?: string;
  zoneName?: string;
  namingContext?: NamingContext;
}

export interface ContributionStats {
  total: number;
  byCategory: Record<string, number>;
  byScale: Record<string, number>;
}

async function get<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`GET ${url} → ${res.status}`);
  return res.json() as Promise<T>;
}

export function fetchBoundary(slug: string): Promise<Record<string, unknown>> {
  return get(`${API_BASE}/${slug}/boundary`);
}

export function fetchNetwork(slug: string): Promise<Record<string, unknown>> {
  return get(`${API_BASE}/${slug}/network`);
}

export function fetchSectors(slug: string): Promise<Record<string, unknown>> {
  return get(`${API_BASE}/${slug}/sectors`);
}

export function fetchStats(slug: string): Promise<ContributionStats> {
  return get(`${API_BASE}/${slug}/contributions/stats`);
}

export async function submitContribution(
  slug: string,
  payload: ContributionPayload,
): Promise<{ id: string; createdAt: string }> {
  const res = await fetch(`${API_BASE}/${slug}/contributions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Error al enviar aporte: ${body}`);
  }
  return res.json() as Promise<{ id: string; createdAt: string }>;
}

// ── Admin API (internal, requires auth) ──────────────────────────────────────

const ADMIN_BASE = '/api/v1/admin/projects';

export interface AdminContributionRow {
  id: string;
  scale: string;
  category: string;
  comment: string | null;
  canonical_edge_id: string | null;
  status: string;
  source: string;
  accessible_mode: boolean;
  location_description: string | null;
  zone_name: string | null;
  street_label: string | null;
  street_name: string | null;
  from_street: string | null;
  to_street: string | null;
  naming_confidence: string | null;
  contact_requested: boolean;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  geometry_geojson: Record<string, unknown> | null;
  created_at: string;
}

export interface AdminContributionsFilter {
  category?: string;
  scale?: string;
  status?: string;
  hasContact?: boolean;
  accessibleMode?: boolean;
  dateFrom?: string;
  dateTo?: string;
  referenceArea?: string;
  text?: string;
  page?: number;
  pageSize?: number;
}

async function authGet<T>(url: string): Promise<T> {
  const token = localStorage.getItem('wayline_token') ?? sessionStorage.getItem('wayline_token') ?? '';
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`GET ${url} → ${res.status}`);
  return res.json() as Promise<T>;
}

export function adminFetchStats(projectId: string): Promise<Record<string, unknown>> {
  return authGet(`${ADMIN_BASE}/${projectId}/contributions/stats`);
}

export function adminFetchContributionsMapData(
  projectId: string,
  filters: Pick<AdminContributionsFilter, 'category' | 'scale' | 'status'> = {},
): Promise<Record<string, unknown>> {
  const params = new URLSearchParams();
  if (filters.category) params.set('category', filters.category);
  if (filters.scale)    params.set('scale', filters.scale);
  if (filters.status)   params.set('status', filters.status);
  const qs = params.toString();
  return authGet(`${ADMIN_BASE}/${projectId}/contributions/map-data.geojson${qs ? `?${qs}` : ''}`);
}

export function adminFetchSegmentsGeoJson(projectId: string): Promise<Record<string, unknown>> {
  return authGet(`${ADMIN_BASE}/${projectId}/contributions/segments.geojson`);
}

export function adminFetchContributions(
  projectId: string,
  filters: AdminContributionsFilter = {},
): Promise<{ data: AdminContributionRow[]; total: number }> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== '') params.set(k, String(v));
  });
  const qs = params.toString();
  return authGet(`${ADMIN_BASE}/${projectId}/contributions${qs ? `?${qs}` : ''}`);
}

export async function adminUpdateStatus(projectId: string, id: string, status: string): Promise<void> {
  const token = localStorage.getItem('wayline_token') ?? sessionStorage.getItem('wayline_token') ?? '';
  const res = await fetch(`${ADMIN_BASE}/${projectId}/contributions/${id}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error(`PATCH status → ${res.status}`);
}

/** Retorna o genera un session_id persistido en localStorage. */
export function getSessionId(): string {
  const key = 'wayline_participation_session';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}
